/**
 * Single source of truth for the bearer token used by the HTTP client and the
 * Socket.IO handshake. The legacy app exposes the backend JWT as
 * `session.user.token`; we mirror that.
 *
 * The value is bridged in from NextAuth by `AuthProvider` (ADR-0005) so the
 * data layer never imports an auth framework.
 *
 * ## The readiness gate
 *
 * `SessionProvider` does not know the session synchronously — it fetches
 * `/api/auth/session` over the network. Until that resolves, `useSession()`
 * reports `status: "loading"` and there is no token to hand out. Any request
 * that fires in that window goes out with no `Authorization` header and comes
 * back 401, which is what made /jobs, /myjobs, /devices (and every other
 * authenticated page) fail on first load and succeed on Retry.
 *
 * So the token is not just a value, it has a *readiness* state, and callers
 * that can wait should wait. `awaitAuthToken()` is that wait; `getAuthToken()`
 * stays synchronous for callers that already handle a null token reactively
 * (the Socket.IO hooks read the session directly instead — see
 * `use-device-live-session.ts`).
 *
 * The gate defaults to OPEN. Nothing waits unless a session bootstrap actually
 * announces itself via `beginAuthBootstrap()`, so tests, server-side rendering
 * and direct repository use are unaffected.
 */
let currentToken: string | null = null;

/** Whether the session bootstrap has settled. Open until proven otherwise. */
let ready = true;
/** Resolvers parked by `awaitAuthToken()` while the gate is closed. */
let waiters: Array<() => void> = [];
let bootstrapTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * How long a bootstrap may hold requests before we give up and let them
 * through unauthenticated. A failed/hanging `/api/auth/session` should surface
 * as a 401 (which now renders a "please log in" state) rather than a page that
 * spins forever.
 */
const BOOTSTRAP_TIMEOUT_MS = 10_000;

/**
 * Close the gate: requests made from here on wait for `markAuthReady()`.
 *
 * Called during `AuthProvider`'s first render — before any page below it can
 * mount and fire a query. Browser-only: module state on the server is shared
 * across requests, and nothing server-side reads this token anyway.
 */
export function beginAuthBootstrap() {
  if (typeof window === "undefined") return;
  if (!ready) return;
  ready = false;
  bootstrapTimer = setTimeout(markAuthReady, BOOTSTRAP_TIMEOUT_MS);
}

/** Open the gate and release every parked request. Idempotent. */
export function markAuthReady() {
  if (ready) return;
  ready = true;
  if (bootstrapTimer !== undefined) {
    clearTimeout(bootstrapTimer);
    bootstrapTimer = undefined;
  }
  const parked = waiters;
  waiters = [];
  for (const release of parked) release();
}

/** True once the session has settled (authenticated or not). */
export function isAuthReady(): boolean {
  return ready;
}

export function setAuthToken(token: string | null) {
  currentToken = token;
}

export function getAuthToken(): string | null {
  return currentToken;
}

/**
 * The token, waited for if the session is still bootstrapping. Returns
 * synchronously when the gate is open so the common case adds no microtask to
 * the request path.
 */
export function awaitAuthToken(): string | null | Promise<string | null> {
  if (ready) return currentToken;
  return new Promise<string | null>((resolve) => {
    waiters.push(() => resolve(currentToken));
  });
}

/** Test seam: drop the token and reopen the gate. */
export function resetAuthToken() {
  currentToken = null;
  if (bootstrapTimer !== undefined) {
    clearTimeout(bootstrapTimer);
    bootstrapTimer = undefined;
  }
  ready = true;
  waiters = [];
}
